/**
 * Export Service
 *
 * Handles exporting user data in various scopes:
 * - Library only (POIs + Tags)
 * - Library + All Trips (full takeout)
 * - Single Trip (trip with referenced POIs)
 */

import { api } from "./api";
import type {
  POIPlannerExport,
  ExportTrip,
  ExportOptions,
  POI,
  Tag,
  CategoryTag,
  AccommodationConfig,
} from "@poi-planner/shared";

// ── Error Handling ──

class ExportError extends Error {
  constructor(
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = "ExportError";
  }
}

// ── Export Functions ──

/**
 * Fetch all POIs from the library
 */
async function fetchLibraryPois(): Promise<POI[]> {
  const library = await api.loadLibrary();
  return library.pois;
}

/**
 * Fetch all tags from the library
 */
async function fetchLibraryTags(): Promise<Tag[]> {
  return api.loadTags();
}

async function fetchLibraryCategoryTags(): Promise<CategoryTag[]> {
  const library = await api.loadLibrary();
  return library.categoryTags;
}

/**
 * Fetch trip data for export
 */
async function fetchTripForExport(tripId: string): Promise<ExportTrip> {
  const tripData = await api.loadTripById(tripId);
  if (!tripData) {
    throw new ExportError("NOT_FOUND", "Trip not found");
  }

  // accommodationConfigs is already a Record in schedule
  const accommodationConfigs: Record<string, AccommodationConfig> = {};
  for (const [poiId, config] of Object.entries(
    tripData.schedule.accommodationConfigs || {},
  )) {
    accommodationConfigs[poiId] = {
      color: config.color,
      checkInTime: config.checkInTime ?? undefined,
      checkOutTime: config.checkOutTime ?? undefined,
    };
  }

  return {
    trip: {
      id: tripData.trip.id,
      name: tripData.trip.name,
      startDate: tripData.trip.startDate,
      endDate: tripData.trip.endDate,
      defaultTimezone: tripData.trip.defaultTimezone,
      timezoneIds: tripData.trip.timezoneIds,
      createdAt: tripData.trip.createdAt,
      updatedAt: tripData.trip.updatedAt,
    },
    schedule: {
      scheduledPOIs: tripData.schedule.scheduledPOIs,
      notes: tripData.schedule.notes,
      accommodationConfigs,
      sleep: tripData.schedule.sleep,
      timezoneOverrides: tripData.schedule.timezoneOverrides,
    },
    // Granular data: legs come back as a top-level sibling of `schedule`,
    // and the exported shape keeps that boundary visible.
    legs: tripData.legs ?? [],
  };
}

/**
 * Fetch all accessible trips for export
 */
async function fetchAllTripsForExport(): Promise<ExportTrip[]> {
  const trips = await api.listTrips();
  const exportTrips: ExportTrip[] = [];

  for (const trip of trips) {
    const exportTrip = await fetchTripForExport(trip.id);
    exportTrips.push(exportTrip);
  }

  return exportTrips;
}

/**
 * Generate export data based on scope
 */
export async function generateExport(
  options: ExportOptions,
): Promise<POIPlannerExport> {
  const exportData: POIPlannerExport = {
    version: 5,
    exportedAt: new Date().toISOString(),
  };

  switch (options.scope) {
    case "library": {
      // Library only
      const pois = await fetchLibraryPois();
      const tags = await fetchLibraryTags();
      const categoryTags = await fetchLibraryCategoryTags();
      exportData.library = { pois, tags, categoryTags };
      break;
    }

    case "all": {
      // Full takeout: library + all trips
      const pois = await fetchLibraryPois();
      const tags = await fetchLibraryTags();
      const categoryTags = await fetchLibraryCategoryTags();
      const trips = await fetchAllTripsForExport();
      exportData.library = { pois, tags, categoryTags };
      exportData.trips = trips;
      break;
    }

    case "trip": {
      // Single trip with referenced POIs
      if (!options.tripId) {
        throw new ExportError(
          "MISSING_TRIP_ID",
          "tripId is required for trip export",
        );
      }

      const trip = await fetchTripForExport(options.tripId);
      const tags = await fetchLibraryTags();
      const categoryTags = await fetchLibraryCategoryTags();

      // Collect all referenced POI IDs
      const poiIds = new Set<string>();
      for (const sp of trip.schedule.scheduledPOIs) {
        poiIds.add(sp.poiId);
      }
      for (const note of trip.schedule.notes) {
        if (note.startPoiId) poiIds.add(note.startPoiId);
        if (note.endPoiId) poiIds.add(note.endPoiId);
      }
      for (const poiId of Object.values(trip.schedule.sleep)) {
        poiIds.add(poiId);
      }
      for (const config of Object.keys(trip.schedule.accommodationConfigs)) {
        poiIds.add(config);
      }
      for (const leg of trip.legs) {
        for (const poiId of leg.poiIds) {
          poiIds.add(poiId);
        }
      }

      // Fetch only referenced POIs
      const allPois = await fetchLibraryPois();
      const referencedPois = allPois.filter((p) => poiIds.has(p.id));

      exportData.library = { pois: referencedPois, tags, categoryTags };
      exportData.trips = [trip];
      break;
    }
  }

  return exportData;
}

/**
 * Download export as JSON file
 */
export async function downloadExport(options: ExportOptions): Promise<void> {
  const data = await generateExport(options);

  // Generate filename
  const date = new Date().toISOString().split("T")[0];
  let filename = `poi-planner-export-${date}`;
  if (options.scope === "trip" && data.trips?.[0]) {
    const tripName = data.trips[0].trip.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-");
    filename = `poi-planner-${tripName}-${date}`;
  }
  filename += ".json";

  // Create blob and download
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
